import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getAllSyncIntegratedSystems, SyncIntegratedSystem } from "../api/sync";
import { useAuth } from "./useAuth";

interface SyncIntegratedSystemsContextType {
  syncIntegratedSystems: SyncIntegratedSystem[];
  isLoading: boolean;
  refetch: () => void;
}

const SyncIntegratedSystemsContext = createContext<
  SyncIntegratedSystemsContextType | undefined
>(undefined);

interface SyncIntegratedSystemsProviderProps {
  children: React.ReactNode;
}

export const SyncIntegratedSystemsProvider: React.FC<SyncIntegratedSystemsProviderProps> =
  ({ children }) => {
    const { isLoggedIn } = useAuth();
    const [syncIntegratedSystems, setSyncIntegratedSystems] = useState<
      SyncIntegratedSystem[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const refetch = useCallback(() => {
      setIsLoading(true);
      getAllSyncIntegratedSystems()
        .then(setSyncIntegratedSystems)
        .catch((err) => console.error(err))
        .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
      if (isLoggedIn) {
        refetch();
      }
    }, [isLoggedIn, refetch]);

    const value = useMemo(
      () => ({ syncIntegratedSystems, isLoading, refetch }),
      [syncIntegratedSystems, isLoading, refetch]
    );

    return (
      <SyncIntegratedSystemsContext.Provider value={value}>
        {children}
      </SyncIntegratedSystemsContext.Provider>
    );
  };

export const useSyncIntegratedSystems =
  (): SyncIntegratedSystemsContextType => {
    const context = useContext(SyncIntegratedSystemsContext);
    if (context === undefined) {
      throw new Error(
        "useSyncIntegratedSystems must be used within a SyncIntegratedSystemsProvider"
      );
    }
    return context;
  };

export const useSyncIntegratedSystem = (syncGroupId: number | undefined) => {
  const { syncIntegratedSystems, isLoading } = useSyncIntegratedSystems();
  const syncIntegratedSystem = useMemo(
    () =>
      syncGroupId
        ? syncIntegratedSystems.find(
            (entry) => entry.sync_group_id === syncGroupId
          )
        : undefined,
    [syncIntegratedSystems, syncGroupId]
  );
  return { syncIntegratedSystem, isLoading };
};
